import { Component, ElementRef, OnInit, QueryList, ViewChildren } from '@angular/core';
import {
  EmbudoMarketing,
  MarketingOrigenService,
  RegistroMarketing,
} from 'src/app/shared/services/marketing-origen.service';

type Rango = '7' | '30' | 'todo';

/** El logo que va al centro del QR: cuadrado, con fondo blanco y buena resolucion. */
const LOGO_QR = 'assets/icons/pwa/icon-512.png';

/** Lado del PNG descargado. Alcanza para imprimir un afiche sin pixelarse. */
const LADO_QR = 1200;

/** Un escalon del embudo, ya con su porcentaje sobre los clics. */
interface Paso {
  nombre: string;
  valor: number;
  pct: number | null;
}

/**
 * Links de campana: el link que se le entrega a Marketing y lo que rindio.
 *
 * Lo miran los operadores, no Marketing. Por eso trae la lista de personas
 * ademas del embudo: el numero se audita persona por persona antes de
 * informarlo.
 */
@Component({
  selector: 'app-marketing-admin',
  templateUrl: './marketing-admin.component.html',
  styleUrls: ['./marketing-admin.component.sass'],
})
export class MarketingAdminComponent implements OnInit {
  rango: Rango = '30';
  cargando = false;
  error = '';

  embudos: EmbudoMarketing[] = [];
  registros: RegistroMarketing[] = [];
  copiado = '';
  descargando = '';

  readonly ladoQr = LADO_QR;

  /** Un `<qrcode>` por link, en canvas y a tamano de descarga. */
  @ViewChildren('qr', { read: ElementRef }) qrs!: QueryList<ElementRef<HTMLElement>>;

  constructor(private marketing: MarketingOrigenService) {}

  ngOnInit(): void {
    void this.cargar();
  }

  async cambiarRango(r: Rango): Promise<void> {
    this.rango = r;
    await this.cargar();
  }

  async cargar(): Promise<void> {
    this.cargando = true;
    this.error = '';
    const desde = this.desde();
    try {
      [this.embudos, this.registros] = await Promise.all([
        this.marketing.embudo(desde),
        this.marketing.registros(desde),
      ]);
    } catch {
      this.error = 'No se pudo cargar el tablero. Probá de nuevo en un momento.';
    } finally {
      this.cargando = false;
    }
  }

  linkDe(origen: string): string {
    return this.marketing.linkDe(origen);
  }

  linkCortoDe(origen: string): string {
    return this.marketing.linkCortoDe(origen);
  }

  /** El link corto sin `https://`, que es como se lee impreso. */
  linkCortoVisible(origen: string): string {
    return this.linkCortoDe(origen).replace(/^https?:\/\//, '');
  }

  async copiar(origen: string, cual: 'corto' | 'largo'): Promise<void> {
    const texto = cual === 'corto' ? this.linkCortoDe(origen) : this.linkDe(origen);
    try {
      await navigator.clipboard.writeText(texto);
      this.copiado = `${origen}:${cual}`;
      setTimeout(() => (this.copiado = ''), 2000);
    } catch {
      this.copiado = '';
    }
  }

  /**
   * Descarga el QR del link corto con el logo al centro y el link escrito
   * abajo, como PNG.
   *
   * El QR se genera con correccion de errores alta (H, ~30%): es lo que
   * permite taparle el centro con el logo y que se siga leyendo. El logo va
   * sobre un cuadrado blanco de 22% del lado (~5% del area), muy por debajo de
   * lo que H tolera.
   */
  async descargarQr(origen: string, indice: number): Promise<void> {
    const qr = this.qrs.get(indice)?.nativeElement.querySelector('canvas');
    if (!qr) return;

    this.descargando = origen;
    try {
      const logo = await this.cargarImagen(LOGO_QR);

      const pie = Math.round(LADO_QR * 0.12);
      const lienzo = document.createElement('canvas');
      lienzo.width = LADO_QR;
      lienzo.height = LADO_QR + pie;
      const ctx = lienzo.getContext('2d');
      if (!ctx) return;

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, lienzo.width, lienzo.height);
      ctx.drawImage(qr, 0, 0, LADO_QR, LADO_QR);

      // Cuadrado blanco redondeado detras del logo, para que los modulos del
      // QR no se vean a traves de el.
      const fondo = Math.round(LADO_QR * 0.22);
      const x = (LADO_QR - fondo) / 2;
      ctx.fillStyle = '#ffffff';
      this.rectanguloRedondeado(ctx, x, x, fondo, fondo, fondo * 0.18);
      ctx.fill();

      // El icono trae su propio margen blanco: se recorta un 12% por lado para
      // que la marca no quede diminuta dentro del cuadrado.
      const recorte = logo.width * 0.12;
      const ladoFuente = logo.width - recorte * 2;
      const ladoLogo = Math.round(fondo * 0.86);
      const xl = (LADO_QR - ladoLogo) / 2;
      ctx.drawImage(logo, recorte, recorte, ladoFuente, ladoFuente, xl, xl, ladoLogo, ladoLogo);

      ctx.fillStyle = '#2F4A2B';
      ctx.font = `600 ${Math.round(pie * 0.42)}px system-ui, -apple-system, "Segoe UI", sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(this.linkCortoVisible(origen), LADO_QR / 2, LADO_QR + pie * 0.4);

      const a = document.createElement('a');
      a.href = lienzo.toDataURL('image/png');
      a.download = `qr-aguacate-${origen}.png`;
      a.click();
    } catch {
      this.error = 'No se pudo generar el QR. Probá de nuevo.';
    } finally {
      this.descargando = '';
    }
  }

  private cargarImagen(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  private rectanguloRedondeado(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number
  ): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  pasos(e: EmbudoMarketing): Paso[] {
    const pct = (n: number) => (e.clics ? Math.round((n / e.clics) * 1000) / 10 : null);
    return [
      { nombre: 'Clics', valor: e.clics, pct: null },
      { nombre: 'Correo verificado', valor: e.correoVerificado, pct: pct(e.correoVerificado) },
      { nombre: 'Registro completo', valor: e.registroCompleto, pct: pct(e.registroCompleto) },
      { nombre: 'KYC aprobado', valor: e.kycAprobado, pct: pct(e.kycAprobado) },
      { nombre: 'Primer fondeo', valor: e.conFondeo, pct: pct(e.conFondeo) },
    ];
  }

  estadoDe(r: RegistroMarketing): string {
    if (r.usuarioId == null) return 'Registro sin terminar';
    return r.estadoCompliance || '—';
  }

  /** Inicio del rango en ISO, o undefined para todo el historico. */
  private desde(): string | undefined {
    if (this.rango === 'todo') return undefined;
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - Number(this.rango) + 1);
    return d.toISOString();
  }
}
